import { Injectable } from '@angular/core';
import { fromEvent as observableFromEvent, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

export interface WindowSize {
  width: number;
  height: number;
}

export interface WindowScroll {
  x: number;
  y: number;
}

interface RedirectOptions {
  newTab?: boolean;
}

declare global {
  interface Window {
    redirect: Function;
  }
}

export enum BeforeInstallPromptChoices {
  Accepted = 'accepted',
  Dismissed = 'dismissed',
}

export interface BeforeInstallPromptChoiceEvent {
  outcome: BeforeInstallPromptChoices;
}

@Injectable({ providedIn: 'root' })
export class WindowService {
  scrollStream: Observable<WindowScroll>;
  windowSize: WindowSize;
  appInstallChoiceStream: ReplaySubject<BeforeInstallPromptChoiceEvent>;

  constructor() {
    this.windowSize = { width: window.innerWidth, height: window.innerHeight };
    const windowScrollStream = observableFromEvent<Event>(window, 'scroll');
    this.appInstallChoiceStream = new ReplaySubject();
    const installPromptStream = observableFromEvent<Event>(window, 'beforeinstallprompt');

    this.scrollStream = windowScrollStream.pipe(
      map((_event): WindowScroll => ({ x: window.pageXOffset, y: window.pageYOffset })),
    );

    installPromptStream.subscribe((event: any) => {
      event.userChoice.then((choice: BeforeInstallPromptChoiceEvent) => {
        this.appInstallChoiceStream.next(choice);
      });
    });
  }

  getBody(): HTMLElement {
    return window.document.body;
  }

  getLocationPathname() {
    return window.location.pathname;
  }

  getUrlQueryParams(): URLSearchParams {
    return new URLSearchParams(window.location.search);
  }

  getLocationHref() {
    return window.location.href;
  }

  getLocationOrigin() {
    return window.location.origin;
  }

  getLocationHrefWithoutOrigin() {
    return this.getLocationHref().substring(this.getLocationOrigin().length);
  }

  getWindowSize() {
    return this.windowSize;
  }

  getCurrentWindowSize() {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  getFrames() {
    return window.frames;
  }

  getURL() {
    return window.URL;
  }

  isAndroid(): boolean {
    return /Android/.test(this.getUserAgent());
  }

  isIos(): boolean {
    const userAgent = this.getUserAgent();
    return /iPad/.test(userAgent) || /iPhone/.test(userAgent);
  }

  isMobile(): boolean {
    return this.isAndroid() || this.isIos();
  }

  isReducedMotionPreferred(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
  }

  isWebKitView(): boolean {
    const isWebKit = (window as any).webkit && (window as any).webkit.messageHandlers;
    return this.isIos() && isWebKit;
  }

  redirect(location: string, options: RedirectOptions = {}) {
    // Adding a method on window that redirects allows us to stub the redirect functionality in
    // cypress tests, allowing us to test external redirects
    if (!window.redirect) {
      window.redirect = (loc: string) => {
        window.location.href = loc;
      };
    }

    const { newTab } = options;
    if (newTab) {
      this.openNewTab(location);
    } else {
      window.redirect(location);
    }
  }

  openNewTab(location: string) {
    window.open(location, '_blank');
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  setTimeout(func: () => void, timeoutMilliseconds: number): number {
    return window.setTimeout(func, timeoutMilliseconds);
  }

  reload() {
    window.location.reload();
  }

  private getUserAgent(): string {
    return navigator.userAgent;
  }
}
